import { useEffect, useState, useRef } from "react";
import { useMitelLivechat } from "@/common/hooks/useMitelLivechat";
import { CHAT_REQUEST, CLOSE_CHAT, CHAT_RESPONSE } from "@/common/events/chatWidgetEvents";
import type { 
  ChatMessage,
  ChatRequestPayload,
  ChatResponsePayload
} from "@/common/types/chat.types";
import { useChatContext } from "@/common/context/ChatContext";

interface MitelLivechatProps {
  addMessageToHistory: (msg: ChatMessage) => void;
  sendMessageToBotBackend: (sender: string, content: string, options?: { autoPost?: boolean }) => void;
  conversationSummary?: string;
  onAgentTyping?: (isTyping: boolean) => void;
}

/**
 * Component that handles all Mitel livechat functionality.
 * This keeps ChatWidget clean by encapsulating all Mitel-specific logic.
 */
export const MitelLivechat = ({ 
  addMessageToHistory,
  sendMessageToBotBackend,
  conversationSummary,
  onAgentTyping
}: MitelLivechatProps) => {
  const { internalEventEmitter } = useChatContext();
  const [isActive, setIsActive] = useState(false);
  const [mitelLiveChatData, setMitelLiveChatData] = useState<any | null>(null);
  
  const isActiveRef = useRef(false);
  const summarySentRef = useRef(false);
  
  const mitel = useMitelLivechat(
    isActive ? mitelLiveChatData : null,
    addMessageToHistory
  );

  useEffect(() => {
    const isConnectedAndActive = isActive && mitel.isConnected();
    const chatStateIsReady = mitel.chatState !== 'idle' || mitel.isConnecting;
    const isReady = isConnectedAndActive && chatStateIsReady;
    
    if (conversationSummary && isReady && !summarySentRef.current) {
      console.log('[MitelLivechat] Sending conversation summary to Mitel (chatState:', mitel.chatState, ', isConnecting:', mitel.isConnecting, ')');
      mitel.sendSummary(conversationSummary);
      summarySentRef.current = true;
    }
  }, [conversationSummary, isActive, mitel, mitel.chatState, mitel.isConnecting]);

  useEffect(() => {
    const handleChatRequest = (payload: ChatRequestPayload) => {
      if (!isActiveRef.current) {
        return;
      }
      
      mitel.sendMessage(payload.content);
      payload.handled = true;
    };

    const handleCloseChat = () => {
      if (isActiveRef.current && mitel) {
        console.log('[MitelLivechat] Disconnecting from Mitel');
        mitel.disconnect();
      }
      isActiveRef.current = false;
      summarySentRef.current = false;
      setIsActive(false);
      setMitelLiveChatData(null);
    };

    const handleChatResponse = (payload: ChatResponsePayload) => {
      if (payload.data.mitelLiveChatData) {
        isActiveRef.current = true;
        summarySentRef.current = false;
        
        setIsActive(true);
        setMitelLiveChatData(payload.data.mitelLiveChatData);
      } else if (payload.data.handover === true) {
        console.warn('[MitelLivechat] ✗ Handover requested but mitelLiveChatData is null');
        
        sendMessageToBotBackend(
          "user",
          "Livechat failed to start - missing mitelLiveChatData",
          { autoPost: true });
      }
    };

    internalEventEmitter.on(CHAT_REQUEST, handleChatRequest);
    internalEventEmitter.on(CHAT_RESPONSE, handleChatResponse);
    internalEventEmitter.on(CLOSE_CHAT, handleCloseChat);

    return () => {
      internalEventEmitter.off(CHAT_REQUEST, handleChatRequest);
      internalEventEmitter.off(CHAT_RESPONSE, handleChatResponse);
      internalEventEmitter.off(CLOSE_CHAT, handleCloseChat);
    };
  }, [isActive]);

  useEffect(() => {
    if (mitel.isAgentTyping) {
      onAgentTyping?.(true);
    } else {
      onAgentTyping?.(false);
    }
  }, [mitel.isAgentTyping, onAgentTyping]);

  useEffect(() => {
    if (mitel.chatState === "terminated" && isActive) {
      isActiveRef.current = false;
      setIsActive(false);
      setMitelLiveChatData(null);
      summarySentRef.current = false;
      sendMessageToBotBackend("user", "Livechat ended.", { autoPost: true });
    }
  }, [mitel.chatState, isActive, sendMessageToBotBackend]);

  return null;
};
